import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { MarketplaceEmailSender } from '@spike/email-marketing-model';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import clsx from 'clsx';
import { FunctionComponent, useRef } from 'react';

import EmojiPickerButton from 'components/UI/V3/EmojiPickerButton';

import styles from './TemplateStepForm.module.scss';

const PREVIEW_MAX_LENGTH = 90;

/**
 * Programmatically insert `text` at the current caret position of an
 * `<input>` or `<textarea>`, then restore focus + caret right after the
 * insertion. The write goes through the native value setter + a synthetic
 * `input` event so React's internal change tracker treats it like real user
 * typing — necessary because V3 `<Input>` keeps a private `useState` seeded
 * from `initialValue` and does not re-sync from prop changes (see the
 * Sender Email comment further down for the original "key swap" workaround).
 * For the native `<textarea>` the same dispatch path works, so a single
 * helper serves both fields.
 */
const insertAtCursor = (el: HTMLInputElement | HTMLTextAreaElement | null, text: string): void => {
    if (!el) return;

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const nextValue = el.value.slice(0, start) + text + el.value.slice(end);

    const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (!setter) return;

    setter.call(el, nextValue);
    el.dispatchEvent(new Event('input', { bubbles: true }));

    // RAF defers selection restoration until after the controlled re-render
    // caused by the input event has flushed — otherwise the re-render can
    // reset the caret to position 0 and undo our work.
    requestAnimationFrame(() => {
        el.focus();
        const caret = start + text.length;
        el.setSelectionRange(caret, caret);
    });
};

interface TemplateStepFormProps {
    subject: string;
    previewText: string;
    senderName: string;
    /** Marketplace-level sender state (null while the slice fetch is in flight). */
    sender: MarketplaceEmailSender | null;
    onChangeSubject: (value: string) => void;
    onChangePreviewText: (value: string) => void;
    onChangeSenderName: (value: string) => void;
    /** Opens the wizard-root EmailSender drawer — wired from every CTA below. */
    onOpenSenderDrawer: () => void;
}

type SenderVariant = 'unconfigured' | 'verified' | 'pending' | 'failed';

const resolveVariant = (sender: MarketplaceEmailSender | null): SenderVariant => {
    if (!sender || !sender.enabled) return 'unconfigured';
    if (sender.status === 'verified') return 'verified';
    if (sender.status === 'pending') return 'pending';
    if (sender.status === 'failed') return 'failed';
    return 'unconfigured';
};

// Empty string means "no address to show" — the Sender Email input renders
// the `placeholder` prop instead. Under the mandatory-sender rule there is
// no legitimate Cuddles-default fallback: an unconfigured sender is a
// blocker the user has to resolve before send, not an implicit "from".
const resolveEffectiveEmail = (sender: MarketplaceEmailSender | null, variant: SenderVariant): string => {
    if (variant === 'unconfigured') return '';
    return sender?.senderEmail ?? '';
};

/**
 * Left column of step 2. Pure controlled form: parent (TemplateStep) owns
 * the state and persists it on Continue.
 *
 * The two "Generate" links are intentionally non-functional placeholders —
 * AI generation lands in a separate ticket (post-MVP). They render the wand
 * icon and label so the layout matches the design now, but the click is a
 * no-op and we can wire it later without touching the surrounding component.
 *
 * The Sender Email input is read-only: the effective address is decided at
 * the marketplace level (via the Email Sender tab), and the value here just
 * mirrors what the BE will actually use at send time. Below the input lives
 * a contextual help line whose copy changes with the sender state:
 *   - unconfigured → invites the user to set up a custom sender
 *   - verified     → offers a "Change" link to swap the address
 *   - pending      → reports verification in progress + link to resolve
 *   - failed       → reports failure + link to fix
 *
 * Every link opens the same Drawer (wizard-root state).
 */
const TemplateStepForm: FunctionComponent<TemplateStepFormProps> = ({
    subject,
    previewText,
    senderName,
    sender,
    onChangeSubject,
    onChangePreviewText,
    onChangeSenderName,
    onOpenSenderDrawer
}) => {
    const variant = resolveVariant(sender);
    const effectiveEmail = resolveEffectiveEmail(sender, variant);

    // Refs target the raw DOM nodes so `insertAtCursor` can read the live
    // selection range and dispatch a synthetic input event. V3 <Input>
    // forwards its ref to the underlying <input>; the textarea is native.
    const subjectInputRef = useRef<HTMLInputElement>(null);
    const previewTextareaRef = useRef<HTMLTextAreaElement>(null);

    return (
        <div className={styles.form}>
            <div className={styles.field}>
                <div className={styles.inputWrapper}>
                    <Input
                        ref={subjectInputRef}
                        label="Subject"
                        required
                        value={subject}
                        onChange={event => onChangeSubject(event.target.value)}
                        maxLength={255}
                    />
                    <EmojiPickerButton
                        className={styles.emojiButton}
                        ariaLabel="Insert emoji into subject"
                        onSelect={emoji => insertAtCursor(subjectInputRef.current, emoji)}
                    />
                </div>
            </div>

            <div className={styles.field}>
                <Label>Preview text</Label>
                <div className={styles.textareaWrapper}>
                    <textarea
                        ref={previewTextareaRef}
                        className={styles.textarea}
                        value={previewText}
                        onChange={event => onChangePreviewText(event.target.value)}
                        maxLength={PREVIEW_MAX_LENGTH}
                        rows={3}
                    />
                    <EmojiPickerButton
                        className={styles.textareaEmojiButton}
                        ariaLabel="Insert emoji into preview text"
                        // Icon sits at the bottom-LEFT of the textarea, so the
                        // picker grows to the RIGHT from the icon — `align='start'`
                        // anchors the popover's left edge to the trigger's left
                        // edge. The default `end` (which the Subject input uses)
                        // would anchor the right edge instead, dragging the
                        // ~350px popover far to the right of the small icon.
                        align="start"
                        onSelect={emoji => insertAtCursor(previewTextareaRef.current, emoji)}
                    />
                    <span className={styles.charCounter}>
                        {previewText.length}/{PREVIEW_MAX_LENGTH}
                    </span>
                </div>
            </div>

            <div className={clsx(styles.field, styles.senderNameField)}>
                <Input
                    label="Sender Name"
                    required
                    value={senderName}
                    onChange={event => onChangeSenderName(event.target.value)}
                    maxLength={100}
                />
            </div>

            <div className={styles.field}>
                {/* `disabled` (not `readOnly`) is what triggers the muted
                 * visual treatment in the V3 Input — it pairs the "you can't
                 * edit this" semantics with the greyed-out look the field
                 * needs to read as auto-derived, not as a typo'd input.
                 *
                 * `key` forces a remount whenever the effective email
                 * changes (e.g. after the user verifies a custom sender in
                 * the drawer). The V3 Input keeps its initial value in
                 * `useState` and never re-syncs when the parent's `value`
                 * prop changes; the key swap is the canonical workaround
                 * already used by `EmailTemplateEditorComponent`. */}
                <Input
                    // Falls back to a stable literal when `effectiveEmail` is
                    // empty (unconfigured) so the key isn't `''` — which would
                    // collide across renders and defeat the remount trick that
                    // makes the V3 Input pick up the new value.
                    key={effectiveEmail || 'placeholder'}
                    label="Sender Email"
                    value={effectiveEmail}
                    placeholder="Set up a sender to continue"
                    onChange={() => undefined}
                    disabled
                />
                <SenderHelp
                    variant={variant}
                    onOpenSenderDrawer={onOpenSenderDrawer}
                />
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// SenderHelp — single-line help text below the read-only Sender Email input.
// Copy is state-driven; the link always opens the wizard-root sender drawer.
// For pending / failed the status word is colored so the user catches the
// problematic state at a glance, without resorting to a heavyweight badge.
// ---------------------------------------------------------------------------

interface SenderHelpProps {
    variant: SenderVariant;
    onOpenSenderDrawer: () => void;
}

const SenderHelp: FunctionComponent<SenderHelpProps> = ({ variant, onOpenSenderDrawer }) => {
    if (variant === 'unconfigured') {
        // Full AlertMessage card — reserved for the most severe sender state
        // (nothing set up yet). Matches the visual treatment used by Step 3
        // and the send confirmation modal so the user sees the same message
        // repeated at every gate. Same sand palette as the other two surfaces.
        return (
            <AlertMessage
                icon={faInfoCircle}
                iconColor="#BAA997"
                bdColor="#BCB8AE"
                bgColor="#F8F5F1"
                className={styles.senderRequiredAlert}
            >
                <span>
                    You need to configure a verified sender before you can send campaigns.{' '}
                    <button
                        type="button"
                        className={styles.senderLink}
                        onClick={onOpenSenderDrawer}
                    >
                        Set up sender
                    </button>
                </span>
            </AlertMessage>
        );
    }

    if (variant === 'verified') {
        return (
            <p className={styles.senderHelpText}>
                <button
                    type="button"
                    className={styles.senderLink}
                    onClick={onOpenSenderDrawer}
                >
                    Change
                </button>
            </p>
        );
    }

    if (variant === 'pending') {
        return (
            <p className={styles.senderHelpText}>
                <span className={styles.senderStatusPending}>Pending verification.</span>{' '}
                <button
                    type="button"
                    className={styles.senderLink}
                    onClick={onOpenSenderDrawer}
                >
                    Check verification
                </button>
            </p>
        );
    }

    // failed
    return (
        <p className={styles.senderHelpText}>
            <span className={styles.senderStatusFailed}>Verification failed.</span>{' '}
            <button
                type="button"
                className={styles.senderLink}
                onClick={onOpenSenderDrawer}
            >
                Fix in Email Sender
            </button>
        </p>
    );
};

export default TemplateStepForm;
